import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, FileText, Plus, Settings, Globe, Menu, X, BookOpen } from "lucide-react";

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };

  const navItems = [
    { to: "/", label: "Expenses", icon: FileText, testid: "nav-dashboard-link", show: true },
    { to: "/expenses/new", label: "New expense", icon: Plus, testid: "nav-new-expense-link", show: true },
    { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings-link", show: user?.role === "admin" },
    { to: "/guide", label: "Guides", icon: BookOpen, testid: "nav-guide-link", show: true },
    { to: "/platform", label: "Platform", icon: Globe, testid: "nav-platform-link", show: user?.is_platform_admin },
  ].filter((i) => i.show);

  const isActive = (to) => location.pathname === to;
  const linkClass = (to) =>
    `px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
      isActive(to) ? "bg-forest-50 text-forest-800" : "text-ink-soft hover:text-forest-800 hover:bg-cream-100"
    }`;

  return (
    <div className="min-h-screen bg-cream-50">
      <header className="sticky top-0 z-30 border-b border-cream-200 bg-cream-50/90 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home-link" onClick={() => setMenuOpen(false)}>
            <div className="h-8 w-8 rounded-md bg-forest-700 grid place-items-center shrink-0">
              <span className="font-heading text-cream-50 text-sm font-semibold">3</span>
            </div>
            <div className="leading-tight">
              <div className="font-heading text-[15px] font-semibold text-forest-800">Sign-Off</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft truncate max-w-[140px] sm:max-w-[180px]">
                {user?.organisation_name || "Charity Expense Approvals"}
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link key={item.to} to={item.to} data-testid={item.testid} className={linkClass(item.to)}>
                <span className="inline-flex items-center gap-1.5"><item.icon className="h-3.5 w-3.5" />{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-xs text-ink-soft">Signed in as {user?.role === "admin" ? "Admin" : "Submitter"}</div>
              <div className="text-sm font-medium text-forest-800 truncate max-w-[180px]" data-testid="user-email-display">{user?.email}</div>
            </div>
            <Button
              variant="outline" size="sm" onClick={handleLogout}
              data-testid="logout-button"
              className="hidden md:inline-flex border-cream-200 hover:bg-cream-100"
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sign out
            </Button>

            {/* Mobile menu toggle */}
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              className="md:hidden h-10 w-10 grid place-items-center rounded-md border border-cream-200 text-forest-800 hover:bg-cream-100"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {menuOpen && (
          <div className="md:hidden border-t border-cream-200 bg-cream-50" data-testid="mobile-menu">
            <nav className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-${item.testid}`}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-2.5 px-3 py-3 rounded-md text-sm font-medium ${
                    isActive(item.to) ? "bg-forest-50 text-forest-800" : "text-ink-soft hover:bg-cream-100"
                  }`}
                >
                  <item.icon className="h-4 w-4" /> {item.label}
                </Link>
              ))}
              <div className="mt-2 pt-3 border-t border-cream-200">
                <div className="px-3 pb-2 text-xs text-ink-soft truncate">{user?.email}</div>
                <button
                  type="button"
                  onClick={handleLogout}
                  data-testid="mobile-logout-button"
                  className="flex items-center gap-2.5 w-full px-3 py-3 rounded-md text-sm font-medium text-rose-700 hover:bg-rose-50"
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 md:px-10 py-6 sm:py-8 md:py-12">{children}</main>

      <footer className="mx-auto max-w-7xl px-4 sm:px-6 md:px-10 py-8 text-xs text-ink-mute">
        Tamper-proof audit trail · Multi-tenant · 2–6 person sign-off
      </footer>
    </div>
  );
}
